import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { SignupShell } from '@/features/auth/components/SignupShell';
import { SignupStepHeader } from '@/features/auth/components/SignupStepHeader';
import { AccountTypeSelect } from '@/features/auth/components/AccountTypeSelect';
import { FieldError, authInputClass } from '@/features/auth/components/FieldError';
import { SignupMobileField } from '@/features/auth/components/SignupMobileField';
import {
  useAccountTypesQuery,
  useOnboardingFieldsQuery,
  useSignupMutation,
} from '@/features/auth/api/useAuthMutations';
import {
  getSignupSteps,
  hasSignupProfileStep,
} from '@/features/auth/utils/signupSteps';
import { getMobileConfig } from '@/features/auth/utils/mobileNumber';
import { PERSONAL_ADVERTISER_ROLE } from '@/shared/config/roles';
import { POST_LOGOUT_REDIRECT_KEY } from '@/shared/lib/auth/clearClientSession';

type SignupFormValues = {
  accountType: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  referralCode?: string;
  acceptTerms: boolean;
};

export function SignupPage() {
  const [searchParams] = useSearchParams();
  const presetAccountType = searchParams.get('accountType') || '';
  const mobileConfig = useMemo(() => getMobileConfig(), []);
  const { data: accountTypesData, isLoading: loadingTypes } =
    useAccountTypesQuery();
  const accountTypes = accountTypesData?.data ?? [];
  const signupMutation = useSignupMutation();

  useEffect(() => {
    try {
      sessionStorage.removeItem(POST_LOGOUT_REDIRECT_KEY);
    } catch {
      // ignore
    }
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<SignupFormValues>({
    mode: 'onChange',
    defaultValues: {
      accountType: '',
      firstName: '',
      lastName: '',
      email: '',
      mobileNumber: '',
      referralCode: '',
      acceptTerms: false,
    },
  });

  useEffect(() => {
    if (!presetAccountType) return;
    if (presetAccountType === PERSONAL_ADVERTISER_ROLE) {
      setValue('accountType', presetAccountType);
      return;
    }
    if (accountTypes.length === 0) return;
    const allowed = accountTypes.some((t) => t.key === presetAccountType);
    if (allowed) setValue('accountType', presetAccountType);
  }, [presetAccountType, accountTypes, setValue]);

  const accountType = watch('accountType');
  const lockAccountType = Boolean(presetAccountType);
  const { data: fieldsData } = useOnboardingFieldsQuery(accountType || null);
  const steps = getSignupSteps(hasSignupProfileStep(fieldsData));

  const onSubmit = (formData: SignupFormValues) => {
    signupMutation.mutate({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      mobileNumber: formData.mobileNumber,
      mobileCountryCode: mobileConfig.code,
      accountType: formData.accountType || presetAccountType,
      referralCode: formData.referralCode || undefined,
    });
  };

  return (
    <SignupShell>
      <div className="signup-form-align flex w-full min-w-0 flex-col">
        <SignupStepHeader
          step={steps.signup}
          totalSteps={steps.totalSteps}
          title="Create account"
          subtitle="Join Screenista in under a minute."
        />

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full min-w-0 flex-col gap-1 text-left"
        >
          {lockAccountType ? (
            <input type="hidden" {...register('accountType')} />
          ) : (
            <div className="w-full min-w-0">
              <label className="mb-2 block text-[13px] font-bold leading-none text-[#2E2A54]">
                Account type <span className="text-red-500">*</span>
              </label>
              <Controller
                name="accountType"
                control={control}
                rules={{ required: 'Account type is required' }}
                render={({ field }) => (
                  <AccountTypeSelect
                    value={field.value}
                    options={accountTypes}
                    loading={loadingTypes}
                    hasError={Boolean(errors.accountType)}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              <FieldError message={errors.accountType?.message} />
            </div>
          )}

          <div className="grid w-full grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-3">
            <div className="min-w-0">
              <label className="mb-2 block text-[13px] font-bold leading-none text-[#2E2A54]">
                First name <span className="text-red-500">*</span>
              </label>
              <input
                placeholder="Alex"
                maxLength={20}
                className={authInputClass(Boolean(errors.firstName))}
                {...register('firstName', {
                  required: 'First name is required',
                  minLength: { value: 3, message: 'Min 3 chars' },
                  maxLength: { value: 20, message: 'Max 20' },
                  pattern: { value: /^[A-Za-z]+$/, message: 'Letters only' },
                })}
              />
              <FieldError message={errors.firstName?.message} />
            </div>
            <div className="min-w-0">
              <label className="mb-2 block text-[13px] font-bold leading-none text-[#2E2A54]">
                Last name <span className="text-red-500">*</span>
              </label>
              <input
                placeholder="Morgan"
                maxLength={20}
                className={authInputClass(Boolean(errors.lastName))}
                {...register('lastName', {
                  required: 'Last name is required',
                  minLength: { value: 3, message: 'Min 3 chars' },
                  maxLength: { value: 20, message: 'Max 20' },
                  pattern: { value: /^[A-Za-z]+$/, message: 'Letters only' },
                })}
              />
              <FieldError message={errors.lastName?.message} />
            </div>
          </div>

          <div className="w-full min-w-0">
            <label className="mb-2 block text-[13px] font-bold leading-none text-[#2E2A54]">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              placeholder="name@company.com"
              className={authInputClass(Boolean(errors.email))}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email',
                },
                maxLength: { value: 254, message: 'Too long' },
              })}
            />
            <FieldError message={errors.email?.message} />
          </div>

          <div className="grid w-full grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-3">
            <SignupMobileField
              name="mobileNumber"
              control={control}
              mobileConfig={mobileConfig}
              errorMessage={errors.mobileNumber?.message}
              setError={setError}
              clearErrors={clearErrors}
            />
            <div className="min-w-0">
              <label className="mb-2 block text-[13px] font-bold leading-none text-[#2E2A54]">
                Referral
              </label>
              <input
                placeholder="Optional"
                className={authInputClass(Boolean(errors.referralCode))}
                {...register('referralCode', {
                  minLength: { value: 3, message: 'Min 3' },
                  maxLength: { value: 15, message: 'Max 15' },
                })}
              />
              <FieldError message={errors.referralCode?.message} />
            </div>
          </div>

          <div className="pt-1">
            <label className="flex cursor-pointer items-start gap-2.5 select-none">
              <input
                type="checkbox"
                className="signup-check mt-[2px] h-[17px] w-[17px] min-w-[17px] cursor-pointer appearance-none rounded-[5px] border-[1.5px] border-[#C7D1CF] transition-[background,border-color]"
                {...register('acceptTerms', {
                  required: 'Please accept terms conditions & privacy policy',
                })}
              />
              <span className="text-[13px] leading-[1.45] text-[#4A4670]">
                I agree to the{' '}
                <a
                  href="/pages/terms-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-[#3A3660] no-underline hover:text-[#3B66B8]"
                >
                  Terms
                </a>{' '}
                &{' '}
                <a
                  href="/pages/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-[#3A3660] no-underline hover:text-[#3B66B8]"
                >
                  Privacy Policy
                </a>{' '}
                <span className="text-red-500">*</span>
              </span>
            </label>
            <FieldError message={errors.acceptTerms?.message} />
          </div>

          <button
            type="submit"
            disabled={signupMutation.isPending}
            className="signup-btn mt-3 flex h-[44px] w-full items-center justify-center rounded-[14px] border-none text-[15px] font-bold text-white transition-all hover:enabled:-translate-y-px active:enabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-45"
          >
            {signupMutation.isPending ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              'Create account'
            )}
          </button>
        </form>

        <p className="mt-5 text-center text-[13px] leading-none text-[#736F8E]">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-bold text-[#2E2A54] no-underline hover:text-[#3B62BD]"
          >
            Sign in
          </Link>
        </p>
      </div>
    </SignupShell>
  );
}
